import React, { useCallback } from 'react'
import { StreamData } from 'types/streamData'
import { WidgetId } from 'features/types'
import { useDashboardWidgetSetting } from 'hooks/useDashboardState'
import CountType from 'enums/CountType'
import { SocialWebStreamWidget } from 'schemas/SocialWebStreamWidgetSchema'
import { AudienceStreamWidget } from 'schemas/AudienceStreamWidgetSchema'
import {
  VocSoundbiteFrame,
  VocSoundbiteNoHeaderFrame
} from 'components/visualization/SoundbiteFrame'
import LinkedVocIcon from 'components/visualization/SoundbiteFrame/DomainIcon/LinkedVocIcon'
import InternalContents from 'components/visualization/SoundbiteFrame/InternalContents/InternalContents'
import HeaderInfo from 'components/visualization/SoundbiteFrame/HeaderInfo'
import StyledAvatar from 'components/visualization/SoundbiteFrame/StyledAvatar'
import LinkedDate from 'components/visualization/SoundbiteFrame/LinkedDate'
import { useMedia } from 'components/visualization/SoundbiteFrame/Media'
import { MediaClickHandler } from 'components/visualization/SoundbiteFrame/Media/MediaGrid'
import ViewOnIcon from 'components/visualization/SoundbiteFrame/ActionIcons/ViewOnIcon'
import MetadataIcons from 'components/visualization/SoundbiteFrame/MetadataIcons'
import Divider from 'components/visualization/SoundbiteFrame/Divider'
import { useAppDispatch } from 'app/hooks'
import { openLightbox } from 'features/ui/uiSlice'

import { useWidgetSettingContext } from '../../widgetSettingContext'

export const VocSoundbite = ({
  widgetId,
  streamData,
  serialNumber
}: {
  widgetId: WidgetId
  streamData: StreamData
  serialNumber: number
}): React.ReactElement => {
  const { defaultMediaDisplayOption, highlighting } = useDashboardWidgetSetting<
    SocialWebStreamWidget | AudienceStreamWidget
  >(widgetId)

  const dispatch = useAppDispatch()
  const mediaClickHandler: MediaClickHandler = useCallback(
    (mediaUrls, index) => {
      dispatch(openLightbox({ slides: mediaUrls, defaultIndex: index }))
    },
    [dispatch]
  )

  const { mediaGrid, mediaController } = useMedia(streamData, {
    defaultDisplayOption: defaultMediaDisplayOption,
    onMediaClick: mediaClickHandler
  })

  const { countType } = useWidgetSettingContext()

  const { isMediaDoc, sourceType, url, domain, title, authorName, authorHandle } = streamData

  const hasActionItems = isMediaDoc || !!(sourceType && url)

  const hasHeader = !!title || !!authorName || !!authorHandle

  if (!hasHeader) {
    return (
      <VocSoundbiteNoHeaderFrame
        avatar={<StyledAvatar streamData={streamData} />}
        dateItem={<LinkedDate streamData={streamData} />}
        domainIcon={<LinkedVocIcon streamData={streamData} />}
        internalContents={
          <>
            {hasHeader && <Divider />}
            <InternalContents streamData={streamData} highlighting={highlighting} />
            {hasActionItems && <Divider />}
          </>
        }
        actionItems={
          hasActionItems && (
            <>
              <Divider />
              {isMediaDoc && mediaController}
              {sourceType && url && <ViewOnIcon source={sourceType!} domain={domain!} url={url} />}
            </>
          )
        }
        metadataItems={
          <MetadataIcons
            streamData={streamData}
            showSentenceTags={countType === CountType.MENTIONS}
          />
        }
        mediaItems={isMediaDoc && mediaGrid}
        serialNumber={serialNumber}
      />
    )
  }

  return (
    <VocSoundbiteFrame
      avatar={<StyledAvatar streamData={streamData} />}
      dateItem={<LinkedDate streamData={streamData} />}
      domainIcon={<LinkedVocIcon streamData={streamData} />}
      headerInfo={<HeaderInfo streamData={streamData} />}
      internalContents={
        <>
          {hasHeader && <Divider />}
          <InternalContents streamData={streamData} highlighting={highlighting} />
          {hasActionItems && <Divider />}
        </>
      }
      actionItems={
        hasActionItems && (
          <>
            {isMediaDoc && mediaController}
            {sourceType && url && <ViewOnIcon source={sourceType!} domain={domain!} url={url} />}
          </>
        )
      }
      metadataItems={
        <MetadataIcons
          streamData={streamData}
          showSentenceTags={countType === CountType.MENTIONS}
        />
      }
      mediaItems={isMediaDoc && mediaGrid}
      serialNumber={serialNumber}
    />
  )
}

export default VocSoundbite
